import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, Users, Flame, ChefHat, Timer, ChevronLeft, ExternalLink } from "lucide-react";
import RecipeCard from "@/components/RecipeCard";
import AdPlaceholder from "@/components/AdPlaceholder";
import { fetchRecipe, fetchRelated, recordView, withAmazonTag } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span
      className="inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0"
      style={{ backgroundColor: "rgba(200, 90, 50, 0.1)", color: "var(--jaf-primary)" }}
    >
      <Icon size={18} />
    </span>
    <div>
      <p className="text-xs uppercase tracking-wider" style={{ color: "var(--jaf-text-muted)" }}>{label}</p>
      <p className="font-serif text-lg leading-none mt-0.5">{value}</p>
    </div>
  </div>
);

export default function RecipeDetailPage() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [recipe, setRecipe] = useState(null);
  const [related, setRelated] = useState([]);
  const [checked, setChecked] = useState({});
  const [notFound, setNotFound] = useState(false);
  const viewedSlugs = useRef(new Set());

  useEffect(() => {
    setNotFound(false);
    setRecipe(null);
    fetchRecipe(slug)
      .then((r) => {
        setRecipe(r);
        document.title = `${r.title} — JustAirFry`;
      })
      .catch(() => setNotFound(true));
    fetchRelated(slug).then(setRelated).catch(() => setRelated([]));
    // Fire-and-forget view tracking — once per slug per browser tab
    if (!viewedSlugs.current.has(slug)) {
      viewedSlugs.current.add(slug);
      recordView(slug).catch(() => {});
    }
    setChecked({});
  }, [slug]);

  if (notFound) {
    return (
      <div className="jaf-container py-24 text-center" data-testid="recipe-not-found">
        <h1 className="font-serif text-4xl">Recipe not found</h1>
        <p className="mt-3" style={{ color: "var(--jaf-text-muted)" }}>
          That one slipped out of the basket. Try our recipe library.
        </p>
        <Link to="/recipes" className="jaf-btn-primary inline-block mt-6 px-6 py-3 rounded-md text-sm font-medium">
          Browse recipes
        </Link>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="jaf-container py-24">
        <div className="h-96 rounded-2xl animate-pulse" style={{ backgroundColor: "var(--jaf-surface)" }} />
      </div>
    );
  }

  // JSON-LD Recipe schema
  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Recipe",
    name: recipe.title,
    image: [recipe.image_url],
    author: { "@type": "Person", name: recipe.author },
    datePublished: recipe.published_at,
    description: recipe.description,
    recipeCategory: recipe.category,
    recipeCuisine: "American",
    prepTime: `PT${recipe.prep_time_minutes}M`,
    cookTime: `PT${recipe.cook_time_minutes}M`,
    totalTime: `PT${recipe.total_time_minutes}M`,
    recipeYield: `${recipe.servings} servings`,
    nutrition: {
      "@type": "NutritionInformation",
      calories: `${recipe.calories} calories`,
    },
    recipeIngredient: recipe.ingredients.map((i) => `${i.amount} ${i.name}`),
    recipeInstructions: recipe.steps.map((s) => ({
      "@type": "HowToStep",
      position: s.number,
      text: s.instruction,
    })),
  };

  const publishedDate = new Date(recipe.published_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <article data-testid="recipe-detail-page">
      <script
        type="application/ld+json"
        data-testid="recipe-schema-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {/* HERO */}
      <section className="relative" data-testid="recipe-hero">
        <div className="jaf-container py-8">
          <Link
            to="/recipes"
            data-testid="back-to-recipes"
            className="inline-flex items-center gap-1 text-sm hover:gap-2 transition-all"
            style={{ color: "var(--jaf-text-muted)" }}
          >
            <ChevronLeft size={16} />
            Back to recipes
          </Link>
        </div>
        <div className="jaf-container grid lg:grid-cols-5 gap-10 pb-10 items-start">
          <div className="lg:col-span-3">
            <img
              src={recipe.image_url}
              alt={recipe.title}
              className="rounded-2xl w-full aspect-[4/3] object-cover shadow-xl"
              data-testid="recipe-hero-image"
            />
          </div>
          <div className="lg:col-span-2">
            <span
              className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-3"
              style={{ color: "var(--jaf-primary)" }}
              data-testid="recipe-category"
            >
              {recipe.category}
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="recipe-title">
              {recipe.title}
            </h1>
            <p
              className="mt-5 text-base sm:text-lg leading-relaxed"
              style={{ color: "var(--jaf-text-muted)" }}
              data-testid="recipe-description"
            >
              {recipe.long_description || recipe.description}
            </p>
            <div className="mt-6 flex items-center gap-3 text-sm" style={{ color: "var(--jaf-text-muted)" }}>
              <span data-testid="recipe-author">By {recipe.author}</span>
              <span aria-hidden="true">·</span>
              <span data-testid="recipe-date">{publishedDate}</span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section
        className="py-6"
        style={{ backgroundColor: "var(--jaf-surface)", borderTop: "1px solid var(--jaf-border)", borderBottom: "1px solid var(--jaf-border)" }}
      >
        <div className="jaf-container grid grid-cols-2 md:grid-cols-5 gap-5">
          <Stat icon={Timer} label="Prep Time" value={`${recipe.prep_time_minutes} min`} testid="stat-prep" />
          <Stat icon={Clock} label="Cook Time" value={`${recipe.cook_time_minutes} min`} testid="stat-cook" />
          <Stat icon={Users} label="Servings" value={recipe.servings} testid="stat-servings" />
          <Stat icon={Flame} label="Calories" value={recipe.calories} testid="stat-calories" />
          <Stat icon={ChefHat} label="Difficulty" value={recipe.difficulty} testid="stat-difficulty" />
        </div>
      </section>

      {/* MAIN GRID */}
      <section className="py-12 md:py-16">
        <div className="jaf-container grid lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2 space-y-12">
            {/* Ingredients */}
            <div data-testid="ingredients-section">
              <h2 className="font-serif text-3xl mb-6">Ingredients</h2>
              <ul className="space-y-3">
                {recipe.ingredients.map((ing, i) => {
                  const id = `ing-${i}`;
                  const isChecked = !!checked[id];
                  return (
                    <li key={id} className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id={id}
                        checked={isChecked}
                        onChange={() => setChecked((s) => ({ ...s, [id]: !s[id] }))}
                        data-testid={`ingredient-check-${i}`}
                        className="mt-1 w-5 h-5 rounded accent-[color:var(--jaf-primary)] cursor-pointer"
                      />
                      <label
                        htmlFor={id}
                        className={`text-base cursor-pointer ${isChecked ? "line-through opacity-60" : ""}`}
                      >
                        <span className="font-medium">{ing.amount}</span>{" "}
                        <span style={{ color: "var(--jaf-text-muted)" }}>{ing.name}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Mobile inline ad */}
            <div className="lg:hidden">
              <AdPlaceholder variant="inline" testId="ad-mobile-inline" />
            </div>

            {/* Instructions */}
            <div data-testid="instructions-section">
              <h2 className="font-serif text-3xl mb-6">Step-by-Step Instructions</h2>
              <ol className="space-y-7">
                {recipe.steps.map((step) => (
                  <li key={step.number} data-testid={`step-${step.number}`}>
                    <div className="flex gap-4">
                      <span className="recipe-step-num">{step.number}</span>
                      <div className="flex-1 pt-1">
                        <p className="text-base leading-relaxed">{step.instruction}</p>
                        {step.tip && (
                          <div className="tip-callout mt-3" data-testid={`tip-${step.number}`}>
                            <span className="font-semibold mr-1">Tip:</span>{step.tip}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Pro tips */}
            {recipe.pro_tips?.length > 0 && (
              <div
                className="rounded-2xl p-7 md:p-9"
                style={{ backgroundColor: "var(--jaf-surface)", border: "1px solid var(--jaf-border)" }}
                data-testid="pro-tips-section"
              >
                <h2 className="font-serif text-2xl md:text-3xl mb-5">Pro Tips from the Kitchen</h2>
                <ul className="space-y-3">
                  {recipe.pro_tips.map((tip, i) => (
                    <li key={i} className="flex gap-3 items-start" data-testid={`pro-tip-${i}`}>
                      <span
                        className="mt-1.5 inline-block h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: "var(--jaf-primary)" }}
                      />
                      <span className="text-base leading-relaxed">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommended product */}
            {recipe.recommended_product && (
              <div
                className="rounded-2xl overflow-hidden grid sm:grid-cols-5"
                style={{ border: "1px solid var(--jaf-border)", backgroundColor: "#fff" }}
                data-testid="recommended-product"
              >
                <div className="sm:col-span-2 bg-[color:var(--jaf-surface)] aspect-square sm:aspect-auto">
                  <img
                    src={recipe.recommended_product.image_url}
                    alt={recipe.recommended_product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="sm:col-span-3 p-6 md:p-8 flex flex-col justify-center">
                  <span
                    className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-2"
                    style={{ color: "var(--jaf-secondary)" }}
                  >
                    Recommended product
                  </span>
                  <h3 className="font-serif text-2xl mb-2">{recipe.recommended_product.name}</h3>
                  <p className="text-sm mb-5" style={{ color: "var(--jaf-text-muted)" }}>
                    {recipe.recommended_product.description}
                  </p>
                  <a
                    href={withAmazonTag(recipe.recommended_product.amazon_url, settings.amazon_associate_tag)}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    data-testid="recommended-product-amazon"
                    className="jaf-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium self-start"
                  >
                    View on Amazon <ExternalLink size={14} />
                  </a>
                  <p className="text-xs mt-3" style={{ color: "var(--jaf-text-muted)" }}>
                    As an Amazon Associate we earn from qualifying purchases.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar ad (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <AdPlaceholder variant="sidebar" testId="ad-desktop-sidebar" />
            </div>
          </aside>
        </div>
      </section>

      {/* Related recipes */}
      {related.length > 0 && (
        <section className="py-16" style={{ backgroundColor: "var(--jaf-surface)" }} data-testid="related-section">
          <div className="jaf-container">
            <h2 className="font-serif text-3xl sm:text-4xl mb-8">You might also like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((r) => (
                <RecipeCard key={r.id} recipe={r} testIdPrefix="related-card" />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
